/**
 * useTrackingEvent.ts
 *
 * Imperative wrapper around @nhst/tracking-lib that emits payloads conforming
 * to the cpp producer contract v1
 * (data-contract/tracking/producer_specific/cpp/cpp_producer_contract_v_1.json).
 *
 * Entry shapes:
 *   - clickEvent / scrollEvent / genericEvent / handleInteractionEvent take a
 *     legacy bag ({ component, elementName, elementId, ...}) for backwards
 *     compatibility with existing call sites; the bag is routed through
 *     legacyToContract() before reaching the library.
 *
 * Templates do NOT call this composable — they use v-track-event /
 * v-track-scroll directives wired in plugins/tracking-lib-directive.ts.
 */
import { trackClick, trackScroll, type TrackingEvent } from '@nhst/tracking-lib';
import { legacyToContract, trackContract, type LegacyBag } from '~/utils/payloadShape';

export const useTrackingEvent = () => {
  const clickEvent = (action: LegacyBag = {}, content: Record<string, unknown> = {}) => {
    trackClick(legacyToContract({ ...action, ...content, action: 'click' }));
  };

  const scrollEvent = (action: LegacyBag = {}, content: Record<string, unknown> = {}) => {
    trackScroll(legacyToContract({ ...action, ...content, action: 'scroll' }));
  };

  const genericEvent = (payload: Record<string, unknown>) => {
    /*
     * genericEvent callers may pass a fully-shaped contract payload OR a legacy bag.
     * Discriminate on `event` only — it's the envelope's discriminator that legacy
     * bags never carry. Using `attributes` was fragile because legacy bags can have it too.
     */
    if (typeof payload.event === 'string') {
      trackContract(payload as TrackingEvent);
      return;
    }
    trackContract(legacyToContract(payload as LegacyBag));
  };

  const handleInteractionEvent = (
    interaction: { action: string; data?: Record<string, unknown> },
    target: HTMLElement | null = null
  ) => {
    const { action, data } = interaction;

    /*
     * eventName is no longer emitted to the new lib (the v2 contract removed
     * attributes.event_name); it's retained here only as the label the legacy
     * useDataLayerEvent path uses (`Click | Header`). The new-lib event is
     * identified by event + attributes.action + component/content instead.
     */
    if (action === 'logo-click') {
      clickEvent({ eventName: 'Header', component: 'header', elementName: 'dn-logo', linkUrl: 'dn.no' });
      return;
    }

    if (action === 'hot-topic-nav-click') {
      let elementPosition = '-1/0';
      if (target) {
        const clickedListItem = target.closest('li');
        const parentElement = target.parentElement?.parentElement;
        if (clickedListItem && parentElement) {
          const allListItems = parentElement.querySelectorAll('li');
          const clickedIndex = Array.from(allListItems).indexOf(clickedListItem as HTMLLIElement);
          if (clickedIndex !== -1) {
            elementPosition = `${clickedIndex + 1}/${allListItems.length}`;
          }
        }
      }
      clickEvent({ eventName: 'Header', component: 'header', elementName: 'dn-navigation-menu', elementPosition });
      return;
    }

    if (action === 'menu-toggle') {
      clickEvent({
        eventName: 'Header',
        component: 'header',
        elementName: 'dn-burger-menu',
        elementId: data?.isOpen ? 'menu-open' : 'menu-close'
      });
      return;
    }

    if (action === 'menu-item-click') {
      clickEvent({
        eventName: 'Header',
        component: 'header',
        elementName: 'dn-burger-menu',
        elementId: 'menu-navigation',
        linkUrl: data?.to as string | undefined
      });
    }
  };

  return {
    clickEvent,
    scrollEvent,
    genericEvent,
    handleInteractionEvent
  };
};
